import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import type {
  OnboardHotelPayload,
  TaxPolicyPayload,
  RoomRateMappingPayload,
  UpdateBookingEnginePayload,
  UploadIconPayload,
  CreateBrandPayload,
  UpdateBrandPayload,
  BrandSetupPayload,
} from "../types";

type ApiError = AxiosError<{ message?: string }>;

export function useMetasearchStatus() {
  return useQuery({
    queryKey: ["metasearch-status"],
    queryFn: () => api.metasearch.getStatus(),
  });
}

export function useAvailableHotels() {
  return useQuery({
    queryKey: ["metasearch-available-hotels"],
    queryFn: () => api.metasearch.getAvailableHotels(),
  });
}

export function useHotelCenterStatus(configId: string | null) {
  return useQuery({
    queryKey: ["metasearch-hotel-center", configId],
    queryFn: () => api.metasearch.getHotelCenterStatus(configId!),
    enabled: !!configId,
  });
}

export function useXmlPreview(configId: string | null) {
  return useQuery({
    queryKey: ["metasearch-xml", configId],
    queryFn: () => api.metasearch.previewXml(configId!),
    enabled: false,
  });
}

export function usePointsOfSalePreview(configId: string | null) {
  return useQuery({
    queryKey: ["metasearch-pos", configId],
    queryFn: () => api.metasearch.previewPointsOfSale(configId!),
    enabled: false,
  });
}

export function useRestrictions(configId: string | null) {
  return useQuery({
    queryKey: ["metasearch-restrictions", configId],
    queryFn: () => api.metasearch.getRestrictions(configId!),
    enabled: !!configId,
  });
}

export function useDiagnostics(days?: number) {
  return useQuery({
    queryKey: ["metasearch-diagnostics", days],
    queryFn: () => api.metasearch.getDiagnostics(days),
  });
}

export function useOnboardHotel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OnboardHotelPayload) => api.metasearch.onboard(data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Hotel onboarded successfully" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
      queryClient.invalidateQueries({ queryKey: ["metasearch-available-hotels"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to onboard hotel",
      });
    },
  });
}

export function useToggleFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, enabled }: { configId: string; enabled: boolean }) =>
      api.metasearch.toggleFeed(configId, enabled),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Feed toggle updated" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to toggle feed",
      });
    },
  });
}

export function useVerifyProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => api.metasearch.verifyProperty(configId),
    onSuccess: (_data, configId) => {
      ShowToast({ type: "success", message: "Property verified" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-hotel-center", configId] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to verify property",
      });
    },
  });
}

export function useTaxPolicies(configId: string) {
  return useQuery({
    queryKey: ["metasearch-tax-policies", configId],
    queryFn: () => api.metasearch.getTaxPolicies(configId),
    enabled: !!configId,
  });
}

export function useRoomRateMappings(configId: string) {
  return useQuery({
    queryKey: ["metasearch-room-rate-mappings", configId],
    queryFn: () => api.metasearch.getRoomRateMappings(configId),
    enabled: !!configId,
  });
}

export function useUpdateTaxPolicies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, taxPolicies }: { configId: string; taxPolicies: TaxPolicyPayload[] }) =>
      api.metasearch.updateTaxPolicies(configId, taxPolicies),
    onSuccess: (_data, variables) => {
      ShowToast({ type: "success", message: "Tax policies updated" });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-tax-policies", variables.configId],
      });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to update tax policies",
      });
    },
  });
}

export function useUpdateRoomRateMappings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      roomRateMappings,
    }: {
      configId: string;
      roomRateMappings: RoomRateMappingPayload[];
    }) => api.metasearch.updateRoomRateMappings(configId, roomRateMappings),
    onSuccess: (_data, variables) => {
      ShowToast({ type: "success", message: "Room-rate mappings updated" });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-room-rate-mappings", variables.configId],
      });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to update mappings",
      });
    },
  });
}

export function useSyncEnrichment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => api.metasearch.syncEnrichment(configId),
    onSuccess: (data: { message?: string }) => {
      ShowToast({
        type: "success",
        message: data?.message || "Enrichment sync queued",
      });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to sync enrichment",
      });
    },
  });
}

export function usePushAri() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => api.metasearch.pushAri(configId),
    onSuccess: (data: { message?: string }) => {
      ShowToast({
        type: "success",
        message: data?.message || "ARI push completed",
      });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to push ARI",
      });
    },
  });
}

export function useUpdateBookingEngine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      data,
    }: {
      configId: string;
      data: UpdateBookingEnginePayload;
    }) => api.metasearch.updateBookingEngine(configId, data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Booking engine updated" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to update booking engine",
      });
    },
  });
}

export function useOffboardHotel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => api.metasearch.offboard(configId),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Hotel removed from MetaSearch" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-available-hotels"],
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to remove hotel",
      });
    },
  });
}

export function useTriggerJob() {
  return useMutation({
    mutationFn: (jobName: string) => api.metasearch.triggerJob(jobName),
    onSuccess: (data: { message?: string }) => {
      ShowToast({
        type: "success",
        message: data?.message || "Job triggered",
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to trigger job",
      });
    },
  });
}

export function useBrandingIcons() {
  return useQuery({
    queryKey: ["metasearch-branding-icons"],
    queryFn: () => api.metasearch.getIcons(),
  });
}

export function useUploadIcon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UploadIconPayload) => api.metasearch.uploadIcon(data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Icon uploaded" });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-icons"],
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to upload icon",
      });
    },
  });
}

export function useBrands() {
  return useQuery({
    queryKey: ["metasearch-branding-brands"],
    queryFn: () => api.metasearch.getBrands(),
  });
}

export function useBrand(brandId: string | null) {
  return useQuery({
    queryKey: ["metasearch-branding-brand", brandId],
    queryFn: () => api.metasearch.getBrand(brandId!),
    enabled: !!brandId,
  });
}

export function useCreateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBrandPayload) => api.metasearch.createBrand(data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Brand created" });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-brands"],
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to create brand",
      });
    },
  });
}

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      brandId,
      data,
    }: {
      brandId: string;
      data: UpdateBrandPayload;
    }) => api.metasearch.updateBrand(brandId, data),
    onSuccess: (_data, variables) => {
      ShowToast({ type: "success", message: "Brand updated" });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-brands"],
      });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-brand", variables.brandId],
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to update brand",
      });
    },
  });
}

export function useUpdateHotelBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      brandId,
    }: {
      configId: string;
      brandId: string | null;
    }) => api.metasearch.updateHotelBrand(configId, brandId),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Hotel brand updated" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-brands"],
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to update hotel brand",
      });
    },
  });
}

export function useBrandSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      configId,
      data,
    }: {
      configId: string;
      data: BrandSetupPayload;
    }) => api.metasearch.brandSetup(configId, data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Brand setup complete" });
      queryClient.invalidateQueries({ queryKey: ["metasearch-status"] });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-brands"],
      });
      queryClient.invalidateQueries({
        queryKey: ["metasearch-branding-icons"],
      });
    },
    onError: (error: ApiError) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Brand setup failed",
      });
    },
  });
}
